import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { DocumentDetailsService } from '@app/dashboard/services/document-details.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InitialData } from '@app/dashboard/models/document-details.model';
import { MatDialog } from '@angular/material/dialog';
import { CommonListDialogComponent } from '@app/dashboard/common-list-dialog/common-list-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  standalone: true,
  selector: 'app-court-form',
  templateUrl: './court-form.component.html',
  styleUrls: ['./court-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatInputModule,
    MatIconModule,
    NgxMatSelectSearchModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatTooltipModule
  ]
})
export class CourtFormComponent implements OnInit {
  @Input() courtOfAppealForm!: FormGroup;
  @Input() trialCourtForm!: FormGroup;
  @Input() isAddReadonly!: boolean;
  @Input() initialData?: InitialData;

   isCourtLoading = false;          // Lower Court (Court of Appeal) fetch
  isCoaDivisionLoading = false;   

  // --- typing/search state ---
  typedJustice = '';
  typedTrialJudge = '';
  isJusticeLoading = false;
  isTrialJudgeLoading = false;

  // --- selections (kept immutable) ---
  panelOfJustices: any[] = [];
  selectedTrialJudges: any[] = [];

  // --- filtered lists for dropdowns ---
  filteredJustices: any[] = [];
  filteredTrialJudges: any[] = [];

  filteredCourts: any[] = [];
  filteredDivisions: any[] = [];
  filteredTrialCourts: any[] = [];
  filteredTrialDivisions: any[] = [];

  // --- master lists (kept separate from form values) ---
  courtOfAppeals: any[] = [];
  courtOfAppealsDivision: any[] = [];
  trialCourts: any[] = [];
  trialCourtsDivisions: any[] = [];
  trialJudges: any[] = [];

  // --- UI bits ---
  isExpanded = false;
  isTrialJudgeExpanded = false;
  today = new Date();

  currentPlaceholder = '';
  private placeholderFullText = '';
  private scrollPosition = 0;
  private scrollInterval: any;

  currentTrialJudgePlaceholder = 'Type judge name...';
  private trialJudgePlaceholderFull = '';
  private trialJudgeScrollPos = 0;
  private trialJudgeScrollInterval: any;

  // search inputs
  courtSearchCtrl = new FormControl('');
  divisionSearchCtrl = new FormControl('');
  trialCourtSearchCtrl = new FormControl('');
  trialDivisionSearchCtrl = new FormControl('');
  trialJudgeSearchCtrl = new FormControl('');

  // toggles to allow "add new" rows
  allowCourtAdd = true;
  allowAddDivision = true;
  allowAddTrialCourt = true;
  allowAddTrialDivision = true;
  allowAddTrialJudge = true;

  constructor(
    private documentDetailsService: DocumentDetailsService,
    private dialog: MatDialog
  ) {}

  private toArray = (v: any) => (Array.isArray(v) ? v : v ? [v] : []);

  ngOnInit() {
    // ensure controls exist
    if (!this.courtOfAppealForm.get('panelOfJustices')) {
      this.courtOfAppealForm.addControl('panelOfJustices', new FormControl([]));
    }
    if (!this.trialCourtForm.get('trialJudge')) {
      this.trialCourtForm.addControl('trialJudge', new FormControl([]));
    }

    // hydrate local arrays from form values (clone to avoid shared refs)
    this.panelOfJustices = [...this.toArray(this.courtOfAppealForm.get('panelOfJustices')?.value)];
    this.selectedTrialJudges = [...this.toArray(this.trialCourtForm.get('trialJudge')?.value)];

    // prefill case no from initialData (readonly path)
    if (this.initialData?.lowerCourtCaseNo) {
      this.courtOfAppealForm.get('courtOfAppealCaseNo')?.setValue(this.initialData.lowerCourtCaseNo);
    }

    this.getcourtMasterData();

    // wire up search filters
    this.filteredTrialCourts = [...this.trialCourts];
    this.filteredTrialDivisions = [...this.trialCourtsDivisions];
    this.filteredTrialJudges = [...this.trialJudges];

    this.trialCourtSearchCtrl.valueChanges.subscribe(v => this.filterTrialCourts(v ?? ''));
    this.trialDivisionSearchCtrl.valueChanges.subscribe(v => this.filterTrialDivisions(v ?? ''));
    this.trialJudgeSearchCtrl.valueChanges.subscribe(v => this.filterTrialJudges(v ?? ''));

    this.filteredCourts = [...this.courtOfAppeals];
    this.courtSearchCtrl.valueChanges.subscribe(v => this.filterCourts(v ?? ''));

    this.divisionSearchCtrl.valueChanges.subscribe(v => this.filterDivisions(v ?? ''));

    // placeholders
    this.updatePanelOnForm();
    this.updateTrialJudgeOnForm();
    this.updatePlaceholderCycle();
    this.updateTrialJudgePlaceholder();
  }

  // close trial-judge dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('#trialJudgeInput') && !target.closest('.trial-judge-dropdown')) {
      this.isTrialJudgeExpanded = false;
      this.filteredTrialJudges = [];
    }
  }
  // Add this inside your CourtFormComponent class
  isRequired(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    if (!control || !control.validator) return false;

    const validator = control.validator({} as any);
    return validator && validator['required'];
  }

  // ---------- master data ----------
  getcourtMasterData() {
    this.isCourtLoading = true;
    
  this.documentDetailsService.getCourtMaster().subscribe({
    next: (response) => {
      const courtData = response?.data ?? [];

      // Master lists
      this.courtOfAppeals = courtData.filter((c: any) => c.courtLevelId === 2);
      this.trialCourts     = courtData.filter((c: any) => c.courtLevelId === 3);

      // Init filtered lists
      this.filteredCourts      = [...this.courtOfAppeals];
      this.filteredTrialCourts = [...this.trialCourts];

      // ---- Set Court of Appeal from initialData.lowerDivision (no default-first) ----
      const lowerDivision = this.initialData?.lowerDivision ?? '';
       if(lowerDivision.toLowerCase().includes('supreme court')){
                 const target = this.courtOfAppeals.find(
                    (c: any) => (c.name || '').trim().toLowerCase() === 'supreme court'
                  );
                  if (target) {
                    // clone to avoid shared refs; also clear division so it can be re-derived
                    this.courtOfAppealForm.patchValue({
                      courtOfAppeal: { ...target },
                      divisionAtCourtOfAppeal: null
                    });
              }}else{
      const [parsedCourtName/*, parsedDivisionName*/] = this.getCourtAndDivision(lowerDivision);

      const coaCtrl = this.courtOfAppealForm.get('courtOfAppeal');
      const existingCoA = coaCtrl?.value;

      // Only set if nothing already selected AND we can find by parsed name
      if (!existingCoA && parsedCourtName) {
        const target = this.courtOfAppeals.find(
          (c: any) => (c.name || '').trim().toLowerCase() === parsedCourtName.trim().toLowerCase()
        );
        if (target) {
          // clone to avoid shared refs; also clear division so it can be re-derived
          this.courtOfAppealForm.patchValue({
            courtOfAppeal: { ...target },
            divisionAtCourtOfAppeal: null
          });
        }
        // If not found, do nothing (no default to the first item)
      }
    }
      // ---- Reconcile existing trial court (kept from your code) ----
      const existingTrialCourt = this.trialCourtForm.get('trialCourt')?.value;
      if (existingTrialCourt) {
        const match = this.findByIdOrName(this.trialCourts, existingTrialCourt);
        if (match) this.trialCourtForm.patchValue({ trialCourt: { ...match } });
      }
      this.isCourtLoading=false;
      this.getDivisionMasterData();
    },
    error: (err) => console.error('Error fetching court master:', err)
  });
}


  private findByIdOrName<T extends { id?: any; name?: string }>(arr: T[], value: any): T | undefined {
    if (!arr?.length || !value) return undefined;
    if (value.id != null) return arr.find(x => x.id === value.id);
    if (value.name) {
      const name = (value.name || '').trim().toLowerCase();
      return arr.find(x => (x.name || '').trim().toLowerCase() === name);
    }
    return undefined;
  }

  getDivisionMasterData() {
     this.isCoaDivisionLoading = true;
    
    const lowerDivision = this.initialData?.lowerDivision;
    this.documentDetailsService.getDivisionMaster().subscribe({
      next: (response) => {
        const divisionData: any[] = response?.data ?? [];
        this.courtOfAppealsDivision = divisionData;

        // CoA divisions filter
        const lowerCourtId = this.courtOfAppealForm.get('courtOfAppeal')?.value?.id;
        if (lowerDivision && lowerCourtId) {
          const filtered = divisionData.filter(d => d.courtId === lowerCourtId);
          if(lowerDivision.toLowerCase().includes('supreme court')){
                 const target = filtered.find((d: any) =>
            (d.name || '').trim().toLowerCase() === 'supreme court'
                  );
                  if (target) {
                    // clone to avoid shared refs; also clear division so it can be re-derived
                    this.courtOfAppealForm.get('divisionAtCourtOfAppeal')?.setValue({ ...target });
              }}else{
          const divisionName = this.getCourtAndDivision(lowerDivision)[1];
          const matched = filtered.find((d: any) =>
            (d.name || '').trim().toLowerCase() === divisionName.trim().toLowerCase()
          );
          if (matched) {
            this.courtOfAppealForm.get('divisionAtCourtOfAppeal')?.setValue({ ...matched });
          }
        }}

        // trial-court divisions filter
        const tc = this.trialCourtForm.get('trialCourt')?.value;
        if (tc?.id) {
          this.filteredTrialDivisions = divisionData.filter(d => d.courtId === tc.id);
          const existingDiv = this.trialCourtForm.get('trialCourtDivision')?.value;
          if (existingDiv) {
            const divMatch = this.findByIdOrName(this.filteredTrialDivisions, existingDiv);
            this.trialCourtForm.patchValue({ trialCourtDivision: divMatch ? { ...divMatch } : null });
          }
        }
        this.isCoaDivisionLoading = false;
      },
      error: (err) => console.error('Error fetching division master:', err)
    });
  }

  // ---------- utilities ----------
  getNames(list: any[]): string {
    if (!Array.isArray(list) || list.length === 0) return '';
    return list.map(item => typeof item === 'string' ? item : item?.name).join(', ');
  }

  openJusticesDialog(judges: any[], isJustices: boolean) {
    const processed = (judges || []).map((j, i) => ({
      sNo: i + 1,
      name: j.name || j.judgeName || '-'
    }));
    this.dialog.open(CommonListDialogComponent, {
      width: '700px',
      disableClose: true,
      data: {
        heading: isJustices ? 'List of Panel Justices' : 'List of Judges',
        displayColumns: ['sNo', 'name'],
        labelData: { sNo: 'S.No', name: 'Judge Name' },
        data: processed
      }
    });
  }

 
  getCourtAndDivision(courtName: string): [string, string] {
  const [court = '', division = ''] = (courtName ?? '')
    .split(/\s*-\s*/, 2)          // split on the first " - "
    .map(s => s.trim());          // trim both parts
  return [court, division];
}
  // ---------- placeholder animation (CoA justices) ----------
  updatePlaceholderCycle() {
    if (this.scrollInterval) clearInterval(this.scrollInterval);
    if (this.panelOfJustices && this.panelOfJustices.length > 0) {
      this.placeholderFullText = this.panelOfJustices.map(j => j.name).join(', ') + ' | ';
      this.currentPlaceholder = this.placeholderFullText;
      setTimeout(() => this.checkIfOverflow(), 50);
    } else {
      this.currentPlaceholder = 'Type justice name...';
    }
  }

  toggleExpand() { this.isExpanded = !this.isExpanded; }

  checkIfOverflow() {
    const measurer = document.getElementById('placeholderMeasurer');
    const inputBox = document.getElementById('justiceInput');
    if (measurer && inputBox) {
      measurer.innerText = this.placeholderFullText;
      const contentWidth = measurer.scrollWidth;
      const boxWidth = inputBox.clientWidth;
      if (contentWidth > boxWidth) this.startRotatingPlaceholder();
    }
  }

  startRotatingPlaceholder() {
    this.scrollPosition = 0;
    this.scrollInterval = setInterval(() => {
      this.currentPlaceholder =
        this.placeholderFullText.slice(this.scrollPosition) +
        this.placeholderFullText.slice(0, this.scrollPosition);
      this.scrollPosition = (this.scrollPosition + 1) % this.placeholderFullText.length;
    }, 200);
  }

  // ---------- placeholder animation (trial judges) ----------
  updateTrialJudgePlaceholder() {
    if (this.trialJudgeScrollInterval) clearInterval(this.trialJudgeScrollInterval);
    if (this.selectedTrialJudges.length) {
      this.trialJudgePlaceholderFull = this.selectedTrialJudges.map(j => j.name).join(', ') + ' | ';
      this.currentTrialJudgePlaceholder = this.trialJudgePlaceholderFull;
      setTimeout(() => this.checkTrialJudgeOverflow(), 50);
    } else {
      this.currentTrialJudgePlaceholder = 'Type judge name...';
    }
  }

  checkTrialJudgeOverflow() {
    const measurer = document.getElementById('placeholderMeasurer');
    const inputBox = document.getElementById('trialJudgeInput');
    if (measurer && inputBox) {
      measurer.innerText = this.trialJudgePlaceholderFull;
      const contentWidth = measurer.scrollWidth;
      const boxWidth = inputBox.clientWidth;
      if (contentWidth > boxWidth) this.startTrialJudgeRotate();
    }
  }

  startTrialJudgeRotate() {
    this.trialJudgeScrollPos = 0;
    this.trialJudgeScrollInterval = setInterval(() => {
      this.currentTrialJudgePlaceholder =
        this.trialJudgePlaceholderFull.slice(this.trialJudgeScrollPos) +
        this.trialJudgePlaceholderFull.slice(0, this.trialJudgeScrollPos);
      this.trialJudgeScrollPos =
        (this.trialJudgeScrollPos + 1) % this.trialJudgePlaceholderFull.length;
    }, 200);
  }

  // ---------- Select filters ----------
  filterCourts(search: string) {
    if (!search) { this.filteredCourts = this.courtOfAppeals; return; }
    const s = search.toLowerCase();
    this.filteredCourts = this.courtOfAppeals.filter(c => (c.name || '').toLowerCase().includes(s));
  }

  filterDivisions(search: string) {
    const selectedCourtId = this.courtOfAppealForm.get('courtOfAppeal')?.value?.id;
    let byCourt = this.courtOfAppealsDivision.filter(d => d.courtId === selectedCourtId);
    if (!search) { this.filteredDivisions = byCourt; return; }
    const s = search.toLowerCase();
    this.filteredDivisions = byCourt.filter(d => (d.name || '').toLowerCase().includes(s));
  }

  filterTrialCourts(search: string) {
    if (!search) { this.filteredTrialCourts = this.trialCourts; return; }
    const s = search.toLowerCase();
    this.filteredTrialCourts = this.trialCourts.filter(c => (c.name || '').toLowerCase().includes(s));
  }

  filterTrialDivisions(search: string) {
    const selectedCourtId = this.trialCourtForm.get('trialCourt')?.value?.id;
    let byCourt = this.courtOfAppealsDivision.filter(d => d.courtId === selectedCourtId);
    if (!search) { this.filteredTrialDivisions = byCourt; return; }
    const s = search.toLowerCase();
    this.filteredTrialDivisions = byCourt.filter(d => (d.name || '').toLowerCase().includes(s));
  }

  filterTrialJudges(search: string) {
    if (!search) { this.filteredTrialJudges = this.trialJudges; return; }
    const s = search.toLowerCase();
    this.filteredTrialJudges = this.trialJudges.filter(j => (j.name || '').toLowerCase().includes(s));
  }

  // ---------- Add new (server) ----------
  addNewCourt(newCourtName: string) {
    const req = { name: newCourtName, courtLevelId: 2 };
    this.documentDetailsService.addCourtMaster(req).subscribe({
      next: (resp) => {
        const created = resp?.data;
        if (created) {
          this.courtOfAppeals = [...this.courtOfAppeals, created];
          this.filteredCourts = [...this.filteredCourts, created];
          this.courtOfAppealForm.get('courtOfAppeal')?.setValue({ ...created });
        }
        this.courtSearchCtrl.setValue('');
      },
      error: (err) => console.error('Error adding court:', err)
    });
  }

  addNewDivision(newDivisionName: string) {
    const court = this.courtOfAppealForm.get('courtOfAppeal')?.value;
    if (!court?.id) return;
    const req = { name: newDivisionName, courtId: court.id };
    this.documentDetailsService.addDivisionMaster(req).subscribe({
      next: (resp) => {
        const created = resp?.data;
        if (created) {
          this.courtOfAppealsDivision = [...this.courtOfAppealsDivision, created];
          this.filteredDivisions = [...this.filteredDivisions, created];
          this.courtOfAppealForm.get('divisionAtCourtOfAppeal')?.setValue({ ...created });
        }
        this.divisionSearchCtrl.setValue('');
      },
      error: (err) => console.error('Error adding division:', err)
    });
  }

  addNewTrialCourt(newName: string) {
    const req = { name: newName, courtLevelId: 3 };
    this.documentDetailsService.addCourtMaster(req).subscribe({
      next: (resp) => {
        const created = resp?.data;
        if (created) {
          this.trialCourts = [...this.trialCourts, created];
          this.filteredTrialCourts = [...this.filteredTrialCourts, created];
          this.trialCourtForm.get('trialCourt')?.setValue({ ...created });
        }
        this.trialCourtSearchCtrl.setValue('');
      },
      error: (err) => console.error('Error adding trial court:', err)
    });
  }

  addNewTrialDivision(newDivisionName: string) {
    const tc = this.trialCourtForm.get('trialCourt')?.value;
    if (!tc?.id) return;
    const req = { name: newDivisionName, courtId: tc.id };
    this.documentDetailsService.addDivisionMaster(req).subscribe({
      next: (resp) => {
        const created = resp?.data;
        if (created) {
          this.courtOfAppealsDivision = [...this.courtOfAppealsDivision, created];
          this.filteredTrialDivisions = [...this.filteredTrialDivisions, created];
          this.trialCourtForm.get('trialCourtDivision')?.setValue({ ...created });
        }
        this.trialDivisionSearchCtrl.setValue('');
      },
      error: (err) => console.error('Error adding trial division:', err)
    });
  }

  // ---------- Selections / compare ----------
  compareById = (a: any, b: any) => (a && b) ? a.id === b.id : a === b;

  onDropdownOpened() {
    this.filteredCourts = [...this.courtOfAppeals];
  }

  onDivisionDropdownOpened() {
    const id = this.courtOfAppealForm.get('courtOfAppeal')?.value?.id;
    this.filteredDivisions = this.courtOfAppealsDivision.filter((d: any) => d.courtId === id);
  }

  onTrialCourtDropdownOpened() {
    this.filteredTrialCourts = [...this.trialCourts];
  }

  onTrialDivisionDropdownOpened() {
    const id = this.trialCourtForm.get('trialCourt')?.value?.id;
    this.filteredTrialDivisions = this.courtOfAppealsDivision.filter((d: any) => d.courtId === id);
  }

  onCourtOfAppealSelected(selected: any) {
    if (selected?.id) {
      this.filteredDivisions = this.courtOfAppealsDivision.filter(d => d.courtId === selected.id);
      this.courtOfAppealForm.patchValue({
        courtOfAppeal: { ...selected },
        divisionAtCourtOfAppeal: null
      });
    } else {
      this.filteredDivisions = [];
      this.courtOfAppealForm.patchValue({ divisionAtCourtOfAppeal: null });
    }
  }

  onTrialCourtSelected(selected: any) {
    if (selected?.id) {
      this.filteredTrialDivisions = this.courtOfAppealsDivision.filter(d => d.courtId === selected.id);
      this.trialCourtForm.patchValue({
        trialCourt: { ...selected },
        trialCourtDivision: null
      });
    } else {
      this.filteredTrialDivisions = [];
      this.trialCourtForm.patchValue({ trialCourtDivision: null });
    }
  }

  onCourtOfAppealDivisionSelected(selected: any) {
    this.courtOfAppealForm.get('divisionAtCourtOfAppeal')?.setValue(selected ? { ...selected } : null);
  }

  onTrialDivisionSelected(selected: any) {
    this.trialCourtForm.get('trialCourtDivision')?.setValue(selected ? { ...selected } : null);
  }

  // ---------- Justice search & selection ----------
  onJusticeInputChange() {
    if ((this.typedJustice?.length ?? 0) >= 4) {
      this.isJusticeLoading = true;
      this.documentDetailsService.getJusticeMaster(this.typedJustice).subscribe({
        next: (response: any) => {
          const data = response?.data ?? [];
          this.filteredJustices = data.filter((j: any) => j.courtLevelId === 2);
          this.isJusticeLoading = false;
        },
        error: () => {
          this.filteredJustices = [];
          this.isJusticeLoading = false;
        }
      });
    } else {
      this.filteredJustices = [];
      this.isJusticeLoading = false;
    }
  }

  selectJustice(justice: any) {
    if (!this.panelOfJustices.some(j => j.id === justice.id)) {
      this.panelOfJustices = [...this.panelOfJustices, justice];
      this.updatePanelOnForm();
      this.updatePlaceholderCycle();
    }
    this.typedJustice = '';
    this.filteredJustices = [];
  }

  addTypedJustice() {
    const value = (this.typedJustice || '').trim();
    if (!value) return;
    this.isJusticeLoading = true;
    this.documentDetailsService.addJustice({ name: value, courtLevelId: 2 }).subscribe({
      next: (resp: any) => {
        const newJustice = resp?.data;
        if (newJustice && !this.panelOfJustices.some(j => j.id === newJustice.id)) {
          this.panelOfJustices = [...this.panelOfJustices, newJustice];
          this.updatePanelOnForm();
          this.updatePlaceholderCycle();
        }
        this.typedJustice = '';
        this.filteredJustices = [];
        this.isJusticeLoading = false;
      },
      error: () => { this.isJusticeLoading = false; }
    });
  }

  removeJustice(justice: any): void {
    this.panelOfJustices = this.panelOfJustices.filter(j => j.id !== justice.id);
    this.updatePanelOnForm();
    this.updatePlaceholderCycle();
  }

  updatePanelOnForm() {
    // write a NEW array instance so Angular sees the change
    this.courtOfAppealForm.get('panelOfJustices')?.setValue([...this.panelOfJustices]);
    this.courtOfAppealForm.get('panelOfJustices')?.markAsDirty();
  }

  // ---------- Trial judge search & selection ----------
  onTrialJudgeInputChange() {
    if ((this.typedTrialJudge?.length ?? 0) >= 4) {
      this.isTrialJudgeLoading = true;
      this.documentDetailsService.getJusticeMaster(this.typedTrialJudge).subscribe({
        next: (response: any) => {
          const data = response?.data ?? [];
          this.filteredTrialJudges = data.filter((j: any) => j.courtLevelId === 3);
          this.isTrialJudgeLoading = false;
        },
        error: () => {
          this.filteredTrialJudges = [];
          this.isTrialJudgeLoading = false;
        }
      });
    } else {
      this.filteredTrialJudges = [];
      this.isTrialJudgeLoading = false;
    }
  }

  selectTrialJudge(judge: any) {
    if (!this.selectedTrialJudges.some(j => j.id === judge.id)) {
      this.selectedTrialJudges = [...this.selectedTrialJudges, judge];
      this.updateTrialJudgeOnForm();
      this.updateTrialJudgePlaceholder();
    }
    this.typedTrialJudge = '';
    this.filteredTrialJudges = [];
  }

  addTypedTrialJudge() {
    const value = (this.typedTrialJudge || '').trim();
    if (!value) return;
    this.isTrialJudgeLoading = true;
    this.documentDetailsService.addJustice({ name: value, courtLevelId: 3 }).subscribe({
      next: (resp: any) => {
        const newJudge = resp?.data;
        if (newJudge && !this.selectedTrialJudges.some(j => j.id === newJudge.id)) {
          this.selectedTrialJudges = [...this.selectedTrialJudges, newJudge];
          this.updateTrialJudgeOnForm();
          this.updateTrialJudgePlaceholder();
        }
        this.typedTrialJudge = '';
        this.filteredTrialJudges = [];
        this.isTrialJudgeLoading = false;
      },
      error: () => { this.isTrialJudgeLoading = false; }
    });
  }

  removeTrialJudge(judge: any) {
    this.selectedTrialJudges = this.selectedTrialJudges.filter(j => j.id !== judge.id);
    this.updateTrialJudgeOnForm();
    this.updateTrialJudgePlaceholder();
  }

  updateTrialJudgeOnForm() {
    this.trialCourtForm.get('trialJudge')?.setValue([...this.selectedTrialJudges]);
    this.trialCourtForm.get('trialJudge')?.markAsDirty();
  }
}
